import { normalizeAttendanceStatus } from "./attendanceApi";

/** Resolved shift from persisted attendance (policy_result_json.schedule) — not a live lookup. */
export function shiftScheduleFromPolicyResult(policyResultJson) {
  const sched = policyResultJson?.schedule;
  if (!sched || typeof sched !== "object") return null;
  if (!sched.expected_in && !sched.shift_code && !sched.shift) return null;
  return {
    templateId: sched.template_id,
    shiftCode: sched.shift_code,
    shiftName: sched.shift,
    expectedIn: sched.expected_in,
    expectedOut: sched.expected_out,
    graceMinutes: sched.grace_minutes,
    resolutionSource: sched.resolution_source,
  };
}

export function formatShiftScheduleLabel(sched) {
  if (!sched) return "";
  const code = sched.shiftCode || sched.shiftName || "Shift";
  const inT = sched.expectedIn || "—";
  const outT = sched.expectedOut || "—";
  return `${code} · ${inT} → ${outT}`;
}

const WEEKDAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

/** Parse API work_date (YYYY-MM-DD or ISO datetime) → { monthKey, day, weekdayIndexMon0 }. */
export function parseWorkDate(workDate) {
  if (!workDate) return null;
  const m = String(workDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const monthKey = `${m[1]}-${m[2]}`;
  const d = new Date(Date.UTC(year, month - 1, day));
  const dow = d.getUTCDay();
  const weekdayIndexMon0 = dow === 0 ? 6 : dow - 1;
  return {
    monthKey,
    day,
    weekdayName: WEEKDAY_NAMES[weekdayIndexMon0],
    weekdayIndexMon0,
  };
}

/** Build day → status map for a given YYYY-MM month from attendance rows. */
export function buildAttendanceDayMap(rows, monthKey) {
  const map = {};
  for (const r of rows || []) {
    const parts = parseWorkDate(r.work_date);
    if (!parts || parts.monthKey !== monthKey) continue;
    const code = normalizeAttendanceStatus(r.status);
    if (code) map[parts.day] = code;
  }
  return map;
}

export function monthRange(monthKey) {
  const [y, m] = monthKey.split("-").map((x) => Number(x));
  if (!y || !m) return null;
  const from = new Date(Date.UTC(y, m - 1, 1));
  const to = new Date(Date.UTC(y, m, 0));
  const pad = (n) => String(n).padStart(2, "0");
  return {
    from_date: `${from.getUTCFullYear()}-${pad(from.getUTCMonth() + 1)}-${pad(from.getUTCDate())}`,
    to_date: `${to.getUTCFullYear()}-${pad(to.getUTCMonth() + 1)}-${pad(to.getUTCDate())}`,
    daysInMonth: to.getUTCDate(),
    year: y,
    month: m,
  };
}

/** Calendar cells: leading blanks + each day with weekday name (week starts Monday). */
export function buildCalendarCells(monthKey) {
  const range = monthRange(monthKey);
  if (!range) return { range: null, cells: [], weekdayHeaders: WEEKDAY_NAMES };

  const first = new Date(Date.UTC(range.year, range.month - 1, 1));
  const startPad = first.getUTCDay() === 0 ? 6 : first.getUTCDay() - 1;
  const cells = [];
  for (let i = 0; i < startPad; i += 1) {
    cells.push({ empty: true, key: `pad-${i}` });
  }
  for (let day = 1; day <= range.daysInMonth; day += 1) {
    const parts = parseWorkDate(`${monthKey}-${String(day).padStart(2, "0")}`);
    cells.push({
      empty: false,
      day,
      weekdayName: parts?.weekdayName || "",
      weekdayIndexMon0: parts?.weekdayIndexMon0 ?? null,
      key: `d-${day}`,
    });
  }
  return { range, cells, weekdayHeaders: WEEKDAY_NAMES };
}

export function summarizeStatusMap(dayMap, daysInMonth) {
  const counts = { P: 0, A: 0, HD: 0, L: 0, WO: 0, H: 0, marked: 0 };
  for (let d = 1; d <= daysInMonth; d += 1) {
    const st = dayMap[d];
    if (!st) continue;
    counts.marked += 1;
    if (counts[st] !== undefined) counts[st] += 1;
  }
  const workingDays = counts.P + counts.HD * 0.5;
  return { ...counts, workingDays, unmarked: daysInMonth - counts.marked };
}

/** Aggregate attendance rows by employee for one month. */
export function aggregateByEmployee(rows, monthKey, employeeNameById) {
  const byEmp = {};
  for (const r of rows || []) {
    const parts = parseWorkDate(r.work_date);
    if (!parts || parts.monthKey !== monthKey) continue;
    const eid = String(r.employee_id);
    if (!byEmp[eid]) {
      byEmp[eid] = {};
    }
    const code = normalizeAttendanceStatus(r.status);
    if (code) byEmp[eid][parts.day] = code;
  }
  return Object.entries(byEmp).map(([employeeId, dayMap]) => {
    const range = monthRange(monthKey);
    const summary = summarizeStatusMap(dayMap, range?.daysInMonth || 30);
    return {
      employeeId,
      employeeName: employeeNameById[employeeId] || employeeId,
      dayMap,
      ...summary,
    };
  });
}

export const WEEKEND_HEADER_STYLES = {
  Saturday: "bg-indigo-100 text-indigo-800",
  Sunday: "bg-rose-100 text-rose-800",
};

export const WEEKEND_CELL_STYLES = {
  Saturday: "bg-indigo-50 border-indigo-200",
  Sunday: "bg-rose-50 border-rose-200",
};

export const HOLIDAY_CELL_STYLE = "bg-violet-100 border-violet-400 ring-1 ring-violet-200";

/** day number → { name, is_optional } for a YYYY-MM month. */
export function buildHolidayDayMap(holidays, monthKey) {
  const map = {};
  for (const h of holidays || []) {
    const parts = parseWorkDate(h.holiday_date);
    if (!parts || parts.monthKey !== monthKey) continue;
    map[parts.day] = {
      name: h.name || "Holiday",
      is_optional: !!h.is_optional,
    };
  }
  return map;
}

/** ISO date (YYYY-MM-DD) → { name, is_optional }. */
export function buildHolidayDateMap(holidays) {
  const map = {};
  for (const h of holidays || []) {
    const m = String(h.holiday_date).match(/^(\d{4}-\d{2}-\d{2})/);
    if (!m) continue;
    map[m[1]] = {
      name: h.name || "Holiday",
      is_optional: !!h.is_optional,
    };
  }
  return map;
}

export function weekdayHeaderClass(weekdayName, weekOffWeekdays) {
  if (isWeekOffWeekdayName(weekdayName, weekOffWeekdays)) {
    return WEEKEND_HEADER_STYLES[weekdayName] || "bg-indigo-100 text-indigo-800";
  }
  return "bg-slate-100 text-slate-600";
}

/** Base cell styling when no attendance status is saved yet. */
export function calendarDayBaseClass(weekdayName, holiday, hasMarkedStatus, weekOffWeekdays) {
  if (hasMarkedStatus) return "";
  if (holiday) return HOLIDAY_CELL_STYLE;
  if (isWeekOffWeekdayName(weekdayName, weekOffWeekdays)) {
    return WEEKEND_CELL_STYLES[weekdayName] || "bg-indigo-50 border-indigo-200";
  }
  return "bg-white border-slate-200";
}

export function weekendPillClass(label, selected, disabled, weekOffWeekdays) {
  if (disabled) return "bg-slate-50 border-slate-100 text-slate-400 cursor-not-allowed";
  if (selected) return "bg-slate-800 text-white border-slate-800";
  const name = label === "Sat" ? "Saturday" : label === "Sun" ? "Sunday" : label;
  const treatAsOff =
    weekOffWeekdays == null
      ? label === "Sat" || label === "Sun"
      : isWeekOffWeekdayName(name, weekOffWeekdays);
  if (treatAsOff) {
    if (label === "Sun" || name === "Sunday") return "bg-rose-50 border-rose-300 text-rose-900 hover:border-rose-400";
    return "bg-indigo-50 border-indigo-300 text-indigo-900 hover:border-indigo-400";
  }
  return "bg-white border-slate-200 text-slate-700 hover:border-slate-300";
}

export function weekOffPolicyFromAttendance(att = {}) {
  return {
    weekOffWeekdays: weekOffWeekdaysFromAttendance(att),
    monthlyOff: monthlyOffConfigFromAttendance(att),
  };
}

export const MONTHLY_OFF_OCCURRENCES = ["1st", "2nd", "3rd", "4th", "Last"];
const WEEKDAY_KEY_TO_INDEX = {
  monday: 0, tuesday: 1, wednesday: 2, thursday: 3,
  friday: 4, saturday: 5, sunday: 6,
};

function normalizeOccurrence(raw) {
  const text = String(raw || "").trim();
  if (text.toLowerCase() === "last") return "Last";
  return ["1st", "2nd", "3rd", "4th"].includes(text) ? text : null;
}

/**
 * Monthly off rules as [{ occurrence, weekday: "saturday" }], mirroring backend
 * `monthly_off_rules_from_attendance`: the rule list first, then the legacy single
 * weekday + occurrences, then `alternate_saturday` (2nd & 4th Saturday).
 */
export function monthlyOffRulesFromAttendance(att = {}) {
  let pairs = [];
  if (Array.isArray(att.monthly_off_rules)) {
    pairs = att.monthly_off_rules.map((r) => [String(r?.weekday || "").trim().toLowerCase(), r?.occurrence]);
  } else if (att.monthly_off_enabled) {
    const weekday = String(att.monthly_off_weekday || "saturday").toLowerCase();
    pairs = (att.monthly_off_occurrences || []).map((occ) => [weekday, occ]);
  } else if (att.alternate_saturday) {
    pairs = [["saturday", "2nd"], ["saturday", "4th"]];
  }
  const seen = new Set();
  const out = [];
  for (const [weekday, occ] of pairs) {
    const occurrence = normalizeOccurrence(occ);
    if (!(weekday in WEEKDAY_KEY_TO_INDEX) || !occurrence) continue;
    const key = `${occurrence}|${weekday}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ occurrence, weekday });
  }
  return out;
}

/** Calendar form of the monthly off rules: { rules: [{ weekday: Mon0 index, occurrence }] } or null. */
export function monthlyOffConfigFromAttendance(att = {}) {
  const rules = monthlyOffRulesFromAttendance(att).map((r) => ({
    weekday: WEEKDAY_KEY_TO_INDEX[r.weekday],
    occurrence: r.occurrence,
  }));
  return rules.length ? { rules } : null;
}

/** 1-based occurrence of a date's weekday in its month, and whether it is the last one. */
export function weekdayOccurrence(year, monthIndex0, day) {
  const lastDay = new Date(Date.UTC(year, monthIndex0 + 1, 0)).getUTCDate();
  return { index: Math.ceil(day / 7), isLast: day + 7 > lastDay };
}

export function isMonthlyOffDay(day, weekdayIndexMon0, monthKey, monthlyOff) {
  if (!monthlyOff) return false;
  // Accept the older { weekday, occurrences } shape as well as { rules }.
  const rules = Array.isArray(monthlyOff.rules)
    ? monthlyOff.rules
    : (monthlyOff.occurrences || []).map((occurrence) => ({ weekday: monthlyOff.weekday, occurrence }));
  const sameDay = rules.filter((r) => r.weekday === weekdayIndexMon0);
  if (!sameDay.length) return false;
  const [y, m] = monthKey.split("-").map(Number);
  const { index, isLast } = weekdayOccurrence(y, m - 1, day);
  const occMap = { "1st": 1, "2nd": 2, "3rd": 3, "4th": 4 };
  return sameDay.some((r) => (r.occurrence === "Last" ? isLast : occMap[r.occurrence] === index));
}

export function weekOffWeekdaysFromAttendance(att = {}) {
  if (Array.isArray(att.week_off_weekdays)) {
    const days = [...new Set(att.week_off_weekdays.map(Number).filter((n) => n >= 0 && n <= 6))];
    if (days.length || String(att.work_week || "").toLowerCase() === "custom") {
      return days;
    }
  }
  const ww = String(att.work_week || "").toLowerCase();
  if (ww === "mon-sat") return [6];
  if (ww === "custom") return [];
  return [5, 6];
}

/**
 * Date-level week-off check mirroring backend `org_week_off_policy.is_week_off_day`:
 * monthly-off pattern → Sunday flag → Saturday (2nd/4th only when alternate_saturday) → other weekdays.
 * `att` is the org attendance settings slice (e.g. GET /organisation/me/week-off-policy).
 */
export function isOrgWeekOffDate(date, att = {}) {
  let offDays;
  if (Array.isArray(att.week_off_weekdays)) {
    offDays = att.week_off_weekdays.map(Number).filter((n) => n >= 0 && n <= 6);
  } else {
    const ww = String(att.work_week || "mon-fri").toLowerCase();
    offDays = ww === "mon-sat" ? [6] : ww === "custom" ? [] : [5, 6];
  }
  const dow = date.getDay();
  const mon0 = dow === 0 ? 6 : dow - 1;
  const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
  if (isMonthlyOffDay(date.getDate(), mon0, monthKey, monthlyOffConfigFromAttendance(att))) return true;
  // Legacy 2nd/4th-Saturday flag only applies until monthly off rules are saved.
  const hasRuleConfig = Array.isArray(att.monthly_off_rules) || att.monthly_off_enabled;
  if (mon0 === 5 && att.alternate_saturday && !hasRuleConfig) {
    const occurrence = Math.ceil(date.getDate() / 7);
    return occurrence === 2 || occurrence === 4;
  }
  return offDays.includes(mon0);
}

export function attendanceDayHoursFromSettings(att = {}) {
  let full = Number(att.full_day_hours);
  let half = Number(att.half_day_hours);
  if (!Number.isFinite(full) || full <= 0) full = 8;
  if (!Number.isFinite(half) || half <= 0) half = full / 2;
  if (half > full) half = full / 2;
  return { fullDayHours: full, halfDayHours: half };
}

export function isWeekOffWeekday(weekdayIndexMon0, weekOffWeekdays) {
  if (!Array.isArray(weekOffWeekdays) || !weekOffWeekdays.length) return false;
  return weekOffWeekdays.map(Number).includes(Number(weekdayIndexMon0));
}

export function isWeekOffWeekdayName(weekdayName, weekOffWeekdays) {
  const idx = WEEKDAY_NAMES.indexOf(weekdayName);
  if (idx < 0) return false;
  return isWeekOffWeekday(idx, weekOffWeekdays);
}

/** Fixed salary divisor (for example 26) when payroll uses fixed working days. */
export function fixedWorkingDaysFromPayroll(payroll = {}) {
  const mode = String(payroll?.total_working_days_mode || "").trim().toLowerCase();
  if (mode !== "fixed") return null;
  const days = Number(payroll?.monthly_salary_divisor_days);
  if (!Number.isFinite(days) || days < 1 || days > 31) return null;
  return days;
}

function patternWeekOffDay(day, weekdayIndexMon0, monthKey, weekOffWeekdays, monthlyOff) {
  if (monthlyOff && monthKey && isMonthlyOffDay(day, weekdayIndexMon0, monthKey, monthlyOff)) return true;
  return isWeekOffWeekday(weekdayIndexMon0, weekOffWeekdays);
}

/**
 * Week off for one calendar date.
 * Mirrors backend `is_week_off_day`: when working days are fixed at 26,
 * only the days left in the month (calendar days − 26) stay week offs.
 * The rest of that weekday is a working day, so working days + holidays = 26.
 */
export function isCalendarWeekOffDay(day, weekdayIndexMon0, monthKey, options = {}) {
  const weekOffWeekdays = options.weekOffWeekdays;
  const monthlyOff = options.monthlyOff || null;
  const fixedWorkingDays = options.fixedWorkingDays;
  if (!patternWeekOffDay(day, weekdayIndexMon0, monthKey, weekOffWeekdays, monthlyOff)) return false;

  const fixed = Number(fixedWorkingDays);
  if (!Number.isFinite(fixed) || fixed <= 0) return true;

  const daysInMonth = monthRange(monthKey)?.daysInMonth;
  if (!daysInMonth) return true;
  const allowance = Math.max(0, daysInMonth - fixed);
  if (allowance <= 0) return false;

  let kept = 0;
  for (let d = 1; d <= day; d += 1) {
    const parts = parseWorkDate(`${monthKey}-${String(d).padStart(2, "0")}`);
    const idx = parts?.weekdayIndexMon0;
    if (patternWeekOffDay(d, idx, monthKey, weekOffWeekdays, monthlyOff)) kept += 1;
  }
  return kept <= allowance;
}

export function mergeAttendanceDisplayMap(dayMap, cells, holidayMap = {}, options = {}) {
  const weekOffWeekdays = options.weekOffWeekdays;
  const monthlyOff = options.monthlyOff || null;
  const monthKey = options.monthKey || null;
  const fixedWorkingDays = options.fixedWorkingDays ?? null;
  const rowsByDay = options.rowsByDay || {};
  const merged = { ...(dayMap || {}) };
  const hasPolicy = Array.isArray(weekOffWeekdays);

  for (const cell of cells || []) {
    if (cell.empty) continue;
    const day = cell.day;
    const weekdayIndex =
      cell.weekdayIndexMon0 ?? WEEKDAY_NAMES.indexOf(cell.weekdayName);
    const row = rowsByDay[day];
    const policyDerived = isPolicyDerivedAttendanceRow(row);
    const existing = merged[day];
    const isOff = isCalendarWeekOffDay(day, weekdayIndex, monthKey, {
      weekOffWeekdays: hasPolicy ? weekOffWeekdays : null,
      monthlyOff,
      fixedWorkingDays,
    });

    const savedByHr = ["P", "A", "HD", "L", "PL", "UL"].includes(existing) && !policyDerived;
    if (holidayMap[day]) {
      if (!savedByHr && (!existing || policyDerived)) merged[day] = "H";
      continue;
    }
    if (!hasPolicy && !monthlyOff) continue;

    if (isOff) {
      if (!savedByHr && (!existing || policyDerived)) merged[day] = "WO";
    } else if (existing === "WO" && policyDerived) {
      delete merged[day];
    }
  }
  return merged;
}

/** True when a list/calendar row is display-only (not saved to attendances). */
export function isInferredAttendanceRow(row) {
  if (!row) return true;
  const source = String(row.attendance_source || row.source || "").toLowerCase();
  if (
    source.includes("fallback") ||
    source.startsWith("default_") ||
    source.includes("inferred")
  ) {
    return true;
  }
  const remarks = String(row.remarks || "").toLowerCase();
  return remarks.startsWith("inferred-");
}

/** True when the row was generated from org calendar policy rather than an HR tap. */
export function isPolicyDerivedAttendanceRow(row) {
  if (!row) return true;
  if (isInferredAttendanceRow(row)) return true;
  const remarks = String(row.remarks || "").toLowerCase();
  return remarks === "calendar";
}

/** True when this calendar day has a real DB attendance row. */
export function hasPersistedAttendance(day, rowsByDay = {}) {
  const row = rowsByDay?.[day];
  if (!row) return false;
  return !isInferredAttendanceRow(row);
}

export { WEEKDAY_NAMES };
